/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import { useLteCellsData } from "./hooks";
import { Button } from "../../../components/ui/button";

import "../Config.css";
import { prepareLteCellDataGrid, prepareRadioNodeDataGrid, tableContainerSxProps } from "../helpers";
import { RadixSelectControl } from "../../../components/ui/radix-select";
import { STATUSLIST } from "../../../globals/globals";
import CustomDataGrid from "../../../components/Shared/Table/CustomDataGrid";
import { setConfirmDialog } from "../../../store/helpers/helperReducers";
import { DIALOGOPENFOR } from "../../../globals/globals";
import { IFullDeviceDetails, ILteCellDataGridRow, IRadioNodesDataGridRow } from "../../../store/api/deviceConfig/types";
import { GridRowParams, GridRowSelectionModel } from "@mui/x-data-grid";
import { confirmDialogSelector } from "../../../store/helpers/helperSelectors";
import { useSelector } from "react-redux";
import ConfirmDialog from "../../../components/Shared/Dialogs/ConfirmDialog";
import { constStr } from "../../../globals/constants";
import { useStoreDispatch } from "../../../store/hooks";
import { prepareActionProps } from "../../../components/Actions/ActionHelper";
import { isOpenRnDrawerSelector, noOfSelectedRowSelector, selectedDeviceSelector, isCellSavedSelector } from "../../../store/api/devices/selectors";
import { lteCellOptions } from "../../../components/Actions/ActionOptions";
import Actions from "../../../components/Actions/Actions";
import { GridEventListener } from "@mui/x-data-grid";
import { setNoOfSelectedRow, setOpenRnDrawer, setSelectedRadioNodeRow, setCellSaved } from "../../../store/api/devices/reducers";
import RnDrawer from "../RadioNode/RnDrawer";
import { Icon } from "../../../components/Shared/Icon";

const LteCells: React.FC = () => {
  const dispatch = useStoreDispatch();
  const {
    permissions,
    data,
    isLoading,
    lteCellsColumns,
    rowClicked,
    setRowClicked,
    setStatus,
    deleteLteCellHandler,
    setSelectedLteCellRow,
    zoneList,
    selectedLteCellDetails,
    setSelectedLteCellDetails,
    radioNodeList,
    cellsData,
    zonesData
  } = useLteCellsData();

  const getDataFromApi = data?.data || [];
  const rowsData = prepareLteCellDataGrid(getDataFromApi, zoneList?.data || []) || [];
  const noOfRows = data?.totalItems || 0;
  //const [NoOfSelectedRow, setNoOfSelectedRow] = React.useState(0);
  const [selectedRow, setSelectedRow] = React.useState<ILteCellDataGridRow>();
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const isOpenRnDrawer = useSelector(isOpenRnDrawerSelector);
  const [radioNodeFormData, setRadioNodeFormData] = React.useState<IRadioNodesDataGridRow | undefined>(undefined);
  const actionProps = prepareActionProps(selectedDevice?.objectKey!, selectedDevice.name, selectedRow, lteCellOptions, "LteCells", false, null);
  const isOfflineDevice = selectedDevice?.configurationStatus?.toLowerCase() === "offline";
  const getRnListFromApi = radioNodeList?.data || [];
  const radioNodesList = getRnListFromApi?.filter((el) => el?.ethernetId !== null) || [];
  const rowsDataRns: Array<IRadioNodesDataGridRow> = prepareRadioNodeDataGrid(radioNodesList) || [];
  const { lteCellRowIds = [] } = useSelector(noOfSelectedRowSelector);
  const isCellSaved = useSelector(isCellSavedSelector);
  const statusOptions = [
    { value: "__placeholder__", label: "STATUS" },
    ...STATUSLIST.map((s) => ({ value: s, label: s })),
  ];
  const [statusValue, setStatusValue] = React.useState("__placeholder__");
  const handleOnStatusSelect = (val: string) => {
    setStatusValue(val);
    setStatus(val === "__placeholder__" ? "" : val);
  };
  const handleOnRowSelected = (rowIds: GridRowSelectionModel) => {

    const rowIdLength = rowIds?.length;
    dispatch(setNoOfSelectedRow({ lteCellRowIds: rowIds }));
    if (rowIdLength === 1) {
      setSelectedRow(() => rowsData.filter((item) => item.id === rowIds[0])[0]);
      setSelectedLteCellRow(() => rowsData.filter((item) => item.id === rowIds[0])[0]);
    }
  };

  const handleRowClicked: GridEventListener<"rowClick"> = (params: GridRowParams<ILteCellDataGridRow>) => {
    if (!permissions?.hasOpCodeGroup("view_topology")) return;
    const row = params.row;
    if (row?.radioNode) {
      const selectedLteCell = getDataFromApi?.filter((el) => el?.index === row?.id?.toString());
      if (selectedLteCell?.length > 0) {
        const arr = rowsDataRns?.filter((el) => el?.id === Number(selectedLteCell[0]?.radioNode)) || [];

        if (arr?.length > 0 && arr[0]?.model?.toLowerCase() !== "unknown" && !!arr[0]?.model) {
          setRadioNodeFormData(arr[0]);
          dispatch(setSelectedRadioNodeRow(arr[0]));
          setSelectedLteCellDetails(selectedLteCell[0]);
          dispatch(setOpenRnDrawer(true));
          setRowClicked(true);
        }
      }
    } else {
      return;
    }
  };

  const handleCloseButtonClicked = (value: boolean) => {
    setRowClicked(value);
  };

  const handleDelete = (event: any) => {
    const isZoneAdded = !!selectedRow?.zoneId && selectedRow?.zoneId !== "";
    if (isZoneAdded) {
      return dispatch(setConfirmDialog(DIALOGOPENFOR.REMOVELTEZONEPOPUP));
    } else {
      return dispatch(setConfirmDialog(DIALOGOPENFOR.LTECELL));
    }
  };

  React.useEffect(() => {
    if (!isOpenRnDrawer && isCellSaved) {
      const UpdatedrowsData = prepareLteCellDataGrid(cellsData?.data || [], zonesData?.data || []) || [];
      if (selectedRow != undefined && UpdatedrowsData.length > 0) {
        setSelectedRow(() => UpdatedrowsData.filter((item) => item.id === selectedRow.id)[0]);
      }
    }
  }, [isOpenRnDrawer, zonesData, cellsData]);

  const handleRemoveZones = (actionName: string) => {
    if (!actionName) return;
    dispatch(setConfirmDialog(undefined));
  };

  return (
    <>
      {openConfirmDialog === DIALOGOPENFOR.LTECELL && (
        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={deleteLteCellHandler}>
          {`Are you sure you want to delete LTE Cell - ${selectedRow?.id}?`}
        </ConfirmDialog>
      )}
      {openConfirmDialog === DIALOGOPENFOR.REMOVELTEZONEPOPUP && (
        <ConfirmDialog actionName={constStr.ok} handleClickAction={handleRemoveZones}>
          Zones are associated with this Cell. To delete, first remove all zones from <strong>{selectedRow?.name || "Unknown"}</strong>?
        </ConfirmDialog>
      )}
      {rowClicked && isOpenRnDrawer && (
        <RnDrawer
          data={radioNodeFormData}
          closedButtonClicked={handleCloseButtonClicked}
          rowsData={rowsDataRns}
          cellList={selectedLteCellDetails}
          isCellDrawer={"snltecell"}
        />
      )}
      <div
        style={{
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius)",
          padding: "18px",
          boxShadow: "var(--shadow-card)",
          backgroundColor: "var(--card)",
        }}
      >
        {lteCellRowIds?.length > 0 ? (
          <div className="pb-4">
            <div className="flex gap-2 items-center">
              <p className="text-sm text-foreground">{`${lteCellRowIds?.length} selected`}</p>
              {permissions?.hasOpCodeGroup("manage_topology") && lteCellRowIds?.length === 1 && (
                <>
                  <Button variant="secondary" size="sm"
                    onClick={handleDelete}
                  >
                    <Icon name="delete" size={16} />
                    Delete
                  </Button>
                  {!!actionProps && <Actions actionProps={actionProps} tableId="lteCell" />}
                </>
              )}
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center pb-2">
              <div className="w-40">
                <RadixSelectControl
                  value={statusValue}
                  onValueChange={handleOnStatusSelect}
                  options={statusOptions}
                  placeholder="STATUS"
                />
              </div>
            </div>
          </div>
        )}
        <div>
          {isLoading && <p>Loading...</p>}
          {!isLoading && (
            <CustomDataGrid
              tableId="lteCell"
              rows={rowsData}
              columns={lteCellsColumns(rowsDataRns || [])}
              checkboxSelection={true}
              rowCount={noOfRows}
              loading={isLoading}
              disableColumnMenu={true}
              disableRowSelectionOnClick={true}
              getRowId={(row: ILteCellDataGridRow) => row.id}
              onRowSelectionModelChange={handleOnRowSelected}
              rowSelectionModel={lteCellRowIds}
              onRowClick={handleRowClicked}
              isRowSelectable={() => !isOfflineDevice}
              sx={{
                "& .MuiDataGrid-virtualScroller": { overflowY: "hidden" },
                "& .MuiDataGrid-scrollbar--vertical": { display: "none" },
              }}
              fillContainer={false}
            />
          )}
        </div>
      </div>
    </>
  );
};
export default LteCells;
